import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useDocumentMeta from '../../utils/useDocumentMeta';
import TermsModal from '../../components/Modals/TermsModal/TermsModal';
import PublicNavbar from '../../components/PublicNavbar/PublicNavbar';
import '../NewLandingPage/LandingPage.css';
import './Pricing.css';

const ClipboardIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
);

const CheckIcon = () => (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" clipRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
    </svg>
);

const LockIcon = () => (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0110 0v4" />
    </svg>
);

const ChevronLeft = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6" />
    </svg>
);

const ChevronRight = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6" />
    </svg>
);

const PLANS = [
    {
        key: 'starter',
        color: 'green',
        monthlyPrice: 12,
        annualPrice: 9,
        features: [
            'prp-plan-starter-f1',
            'prp-plan-starter-f2',
            'prp-plan-starter-f3',
            'prp-plan-starter-f4',
        ],
    },
    {
        key: 'professional',
        color: 'orange',
        monthlyPrice: 24,
        annualPrice: 19,
        popular: true,
        features: [
            'prp-plan-professional-f1',
            'prp-plan-professional-f2',
            'prp-plan-professional-f3',
            'prp-plan-professional-f4',
            'prp-plan-professional-f5',
        ],
    },
    {
        key: 'clinic',
        color: 'blue',
        monthlyPrice: 59,
        annualPrice: 49,
        features: [
            'prp-plan-clinic-f1',
            'prp-plan-clinic-f2',
            'prp-plan-clinic-f3',
            'prp-plan-clinic-f4',
        ],
    },
];

const Pricing = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [activeIndex, setActiveIndex] = useState(0);
    const [showTermsModal, setShowTermsModal] = useState(false);
    useDocumentMeta('seo-pricing-title', 'seo-pricing-desc', { canonicalPath: '/pricing' });

    const goPrev = () => setActiveIndex(i => (i - 1 + PLANS.length) % PLANS.length);
    const goNext = () => setActiveIndex(i => (i + 1) % PLANS.length);

    return (
        <>
        <PublicNavbar />
        <div className="prp nlp">

            {/* ── HERO ── */}
            <header className="prp-hero">
                <div className="nlp-container">
                    <span className="nlp-badge nlp-badge-orange">{t('prp-badge')}</span>
                    <h1 className="prp-hero-title">{t('prp-hero-title')}</h1>
                    <p className="prp-hero-subtitle">{t('prp-hero-subtitle')}</p>
                </div>
            </header>

            {/* ── FREE LAUNCH BANNER ── */}
            <div className="prp-free-section">
                <div className="nlp-container">
                    <div className="prp-free-card">
                        <div className="prp-free-emoji" aria-hidden="true">🎉</div>
                        <div className="prp-free-text">
                            <h2>{t('prp-free-title')}</h2>
                            <p>{t('prp-free-body')}</p>
                        </div>
                        <button className="prp-free-cta-btn" onClick={() => navigate('/register')}>
                            {t('prp-free-cta')}
                        </button>
                    </div>
                </div>
            </div>

            {/* ── PLANS SECTION ── */}
            <section className="prp-section">
                <div className="nlp-container">
                    <h2 className="prp-section-heading">{t('prp-plans-heading')}</h2>
                    <p className="prp-section-subheading">{t('prp-plans-subheading')}</p>

                    {/* Billing cycle tabs */}
                    <div className="prp-billing-tabs" role="tablist">
                        <button
                            role="tab"
                            aria-selected={billingCycle === 'monthly'}
                            className={`prp-tab${billingCycle === 'monthly' ? ' prp-tab-active' : ''}`}
                            onClick={() => setBillingCycle('monthly')}
                        >
                            {t('prp-tab-monthly')}
                        </button>
                        <button
                            role="tab"
                            aria-selected={billingCycle === 'annual'}
                            className={`prp-tab${billingCycle === 'annual' ? ' prp-tab-active' : ''}`}
                            onClick={() => setBillingCycle('annual')}
                        >
                            {t('prp-tab-annual')}
                            <span className="prp-save-badge">{t('prp-tab-annual-save')}</span>
                        </button>
                    </div>

                    {/* Cards wrapper — obscured by overlay */}
                    <div className="prp-plans-wrapper">

                        {/* Frosted overlay */}
                        <div className="prp-overlay" aria-hidden="true">
                            <div className="prp-overlay-icon"><LockIcon /></div>
                            <p className="prp-overlay-title">{t('prp-overlay-title')}</p>
                            <p className="prp-overlay-body">{t('prp-overlay-body')}</p>
                        </div>

                        {/* Carousel track */}
                        <div
                            className="prp-cards-track"
                            style={{ transform: `translateX(calc(-${activeIndex} * 100%))` }}
                            aria-hidden="true"
                        >
                            {PLANS.map((plan) => {
                                const price = billingCycle === 'monthly' ? plan.monthlyPrice : plan.annualPrice;
                                return (
                                    <div key={plan.key} className={`prp-plan-card${plan.popular ? ' prp-plan-popular' : ''}`}>
                                        {plan.popular && (
                                            <div className="prp-popular-badge">{t('prp-popular-badge')}</div>
                                        )}
                                        <div className="prp-plan-header">
                                            <span className={`nlp-badge nlp-badge-${plan.color}`}>
                                                {t(`prp-plan-${plan.key}-name`)}
                                            </span>
                                            <div className="prp-price">
                                                <span className="prp-price-currency">€</span>
                                                <span className="prp-price-amount">{price}</span>
                                                <span className="prp-price-period">/{t('prp-per-month')}</span>
                                            </div>
                                            {billingCycle === 'annual' && (
                                                <span className="prp-billed-annually">{t('prp-billed-annually')}</span>
                                            )}
                                            <p className="prp-plan-desc">{t(`prp-plan-${plan.key}-desc`)}</p>
                                        </div>
                                        <ul className="prp-features">
                                            {plan.features.map((fKey) => (
                                                <li key={fKey}>
                                                    <CheckIcon />
                                                    {t(fKey)}
                                                </li>
                                            ))}
                                        </ul>
                                        <button className="prp-plan-btn" disabled tabIndex={-1}>
                                            {t('prp-plan-coming-soon')}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Mobile carousel navigation */}
                    <div className="prp-carousel-nav">
                        <button className="prp-carousel-arrow" onClick={goPrev} aria-label="Previous plan">
                            <ChevronLeft />
                        </button>
                        <div className="prp-carousel-dots">
                            {PLANS.map((_, i) => (
                                <button
                                    key={i}
                                    className={`prp-dot${i === activeIndex ? ' prp-dot-active' : ''}`}
                                    onClick={() => setActiveIndex(i)}
                                    aria-label={`Plan ${i + 1}`}
                                />
                            ))}
                        </div>
                        <button className="prp-carousel-arrow" onClick={goNext} aria-label="Next plan">
                            <ChevronRight />
                        </button>
                    </div>

                </div>
            </section>

            {/* ── FOOTER ── */}
            <footer className="nlp-footer">
                <div className="nlp-container">
                    <div className="nlp-footer-inner">
                        <div className="nlp-footer-logo">
                            <div className="nlp-footer-logo-icon"><ClipboardIcon /></div>
                            <span className="nlp-footer-logo-text">Dietist<span>Pro</span></span>
                        </div>
                        <div className="nlp-footer-links">
                            <a href="#" onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}>{t('nlp-footer-privacy')}</a>
                            <a href="#" onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}>{t('nlp-footer-terms')}</a>
                        </div>
                        <span className="nlp-footer-copy">{t('nlp-footer-copy')}</span>
                    </div>
                </div>
            </footer>

        </div>

        <TermsModal open={showTermsModal} onClose={() => setShowTermsModal(false)} />
        </>
    );
};

export default Pricing;
